import { useState, useEffect } from 'react';
import { FirebaseError } from 'firebase/app';

/**
 * Network status hook
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Offline state manager - DISABLED
 * We don't want to show offline notifications for normal Firebase operation
 */
class OfflineManager {
  private isOffline = false;
  private listeners: Set<(offline: boolean) => void> = new Set();

  setOffline(offline: boolean) {
    // DISABLED: Don't trigger offline state for Firebase errors
    // These are handled by retry logic instead
    return;
  }

  getOffline() {
    return false; // Always return false - no Firebase offline notifications
  }

  subscribe(listener: (offline: boolean) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.isOffline));
  }
}

export const offlineManager = new OfflineManager();

/**
 * Hook to track Firebase offline status
 */
export function useFirebaseOfflineStatus() {
  const [isOffline, setIsOffline] = useState(offlineManager.getOffline());

  useEffect(() => {
    const unsubscribe = offlineManager.subscribe(setIsOffline);
    return () => {
      unsubscribe();
    };
  }, []);

  return isOffline;
}

/**
 * Gets user-friendly error message for Firebase errors
 */
export function getFirebaseErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return 'An unexpected error occurred. Please try again.';
  }

  const errorMessages: Record<string, string> = {
    'unavailable': 'Service temporarily unavailable. Please check your internet connection.',
    'deadline-exceeded': 'Request timed out. Please try again.',
    'permission-denied': 'You do not have permission to access this resource.',
    'not-found': 'The requested data was not found.',
    'already-exists': 'This item already exists.',
    'resource-exhausted': 'Too many requests. Please wait a moment and try again.',
    'failed-precondition': 'Operation cannot be completed at this time.',
    'aborted': 'Operation was aborted. Please try again.',
    'out-of-range': 'Invalid data range provided.',
    'unimplemented': 'This feature is not yet implemented.',
    'internal': 'An internal error occurred. Please try again later.',
    'unauthenticated': 'Please sign in to continue.',
    'cancelled': 'Operation was cancelled.'
  };

  return errorMessages[error.code] || 'An error occurred. Please try again.';
}

/**
 * Detects if error is due to offline status
 */
export function isOfflineError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) {
    return false;
  }

  return (
    error.code === 'unavailable' ||
    error.message.includes('client is offline') ||
    error.message.includes('network') ||
    error.message.includes('backend')
  );
}

/**
 * Handles Firebase errors (no longer updates offline status)
 */
export function handleFirebaseError(error: unknown): never {
  // Just throw the error - don't trigger offline notifications
  throw error;
}

/**
 * Wraps a Firebase operation (no offline detection)
 * Offline detection is disabled - retry logic handles failures
 */
export async function withOfflineDetection<T>(
  operation: () => Promise<T>
): Promise<T> {
  try {
    const result = await operation();
    return result;
  } catch (error) {
    // Don't trigger offline state - just let retry logic handle it
    throw error;
  }
}
